import React, { useState, useEffect, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, X, Info, AlertTriangle } from 'lucide-react';

// Toast Context for global usage
const ToastContext = createContext(null);

// Toast types and their styles
const toastStyles = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-800',
    subtext: 'text-green-700',
    icon: CheckCircle,
    iconColor: 'text-green-500'
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-800',
    subtext: 'text-red-700',
    icon: AlertCircle,
    iconColor: 'text-red-500'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-500',
    text: 'text-yellow-800',
    subtext: 'text-yellow-700',
    icon: AlertTriangle,
    iconColor: 'text-yellow-500'
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    text: 'text-blue-800',
    subtext: 'text-blue-700',
    icon: Info,
    iconColor: 'text-blue-500'
  }
};

// Helper function to extract error message from various formats
export const getErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data;
    // Handle different error formats
    if (typeof data === 'string') return data;
    if (data.detail) {
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail)) {
        return data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      }
      return JSON.stringify(data.detail);
    }
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
  }
  return error.message || 'An unexpected error occurred';
};

// Individual Toast Component
export const Toast = ({ toast, onClose }) => {
  const style = toastStyles[toast.type] || toastStyles.info;
  const Icon = style.icon;

  return (
    <div 
      className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg border-l-4 ${style.bg} ${style.border} animate-slide-in`}
      data-testid={`toast-${toast.type}`}
    >
      <Icon className={`w-6 h-6 ${style.iconColor} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className={`font-semibold ${style.text}`}>
          {toast.type === 'error' ? 'Error' : toast.type === 'success' ? 'Success' : toast.type === 'warning' ? 'Warning' : 'Info'}
        </p>
        <p className={`text-sm ${style.subtext} break-words`}>
          {toast.message}
        </p>
      </div>
      <button 
        onClick={onClose}
        className={`p-1 rounded-full hover:bg-black hover:bg-opacity-10 flex-shrink-0`}
        data-testid="toast-close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

// Toast Container Component
export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md" data-testid="toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

// Custom hook for using toast
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'success', duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const ToastWrapper = () => (
    <ToastContainer toasts={toasts} removeToast={removeToast} />
  );

  return { showToast, removeToast, ToastWrapper, toasts };
};

// Simple inline toast hook for single component usage
export const useSimpleToast = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const ToastComponent = () => toast.show ? (
    <div className="fixed top-4 right-4 z-50">
      <Toast 
        toast={{ id: 1, message: toast.message, type: toast.type }} 
        onClose={hideToast} 
      />
    </div>
  ) : null;

  return { showToast, hideToast, toast, ToastComponent };
};

export default Toast;
